import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2 as Renderer,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  BatteryModelEnum,
  RDSAlertsStatus,
  RDSAzzurroEvents,
  RDSDeviceSortEnum,
  RDSDevicesSortInput,
  RDSDevicesStatusFilter,
  RDSDeviceStatus,
  RDSDeviceTypeEnum,
  RDSPaginationInput,
  RDSSortDirectionEnum,
  SystemsService,
} from 'ngx-core-services';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import { ColumnConfig, ColorTypeEnum, ColumnTypeEnum } from 'ngx-zcs-components';
import { BehaviorSubject, combineLatest, from, Subject } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import {
  ChipData,
  compareInverterFWModuleVersions,
  FirmwareComparison,
  InverterUpgradeConfig,
} from 'ngx-modbus';
import { AccessSupportService } from '../../communication/access-support.service';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { FormBuilder, FormGroup } from '@angular/forms';

import { DeviceRegistryService } from './device-registry.service';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';

const getLabels = () => ({
  actions: 'Azioni',
  alarms: 'Alarms',
  viewDevice: 'Visualizza',
  applyFilters: 'applica filtri',
  armPlaceholder: 'ARM',
  azzurroEventNotPresent: 'Azzurro Event Not Present',
  azzurroEventPresent: 'Azzurro Event Present',
  azzurroEventsFilterLabel: 'Azzurro Events',
  batteryModel: 'Battery Model',
  batteryModelFilterPlaceholder: 'Battery Model',
  deviceFilters: 'Filtri dispositivo',
  deviceId: 'Device ID',
  deviceIdPlaceholder: 'Device ID',
  deviceRegistryTitle: 'lista dispositivi',
  deviceType: 'Device Type',
  deviceTypeFilterPlaceholder: 'Device Type',
  dspmPlaceholder: 'DSPM',
  dspsPlaceholder: 'DSPS',
  firmwareFilters: 'Filtri firmware',
  hasAzzurroEvents: 'Has Azzurro Events',
  inAlarmFilterLabel: 'In Alarm',
  isInAlarm: 'Is in alarm',
  itemsPerPageLabel: 'Elementi per pagina',
  noAlarm: 'No Alarm',
  noData: 'Nessun dato disponibile',
  noResults: 'Nessun risultato trovato',
  noResultsFound: 'Non sono stati trovati risultati',
  of: 'di',
  resetFilters: 'resetta filtri',
  search: 'Cerca',
  selectColumns: 'Seleziona colonne',
  stateFilters: 'Filtri stato',
  updatedAt: 'Last Update',
});

export const getChipColor = async (
  row: RDSDeviceStatus | RDSAzzurroEvents | RDSAlertsStatus,
  moduleName: 'arm' | 'dspm' | 'dsps',
  accessSupportService: AccessSupportService
): Promise<ColorTypeEnum> => {
  let isLatest = true;
  try {
    const deviceUpgradeConfig = (await accessSupportService.getDeviceModuleInfo(row.deviceId)) as InverterUpgradeConfig;
    if (row[moduleName] === null || row[moduleName] === '') {
      return ColorTypeEnum.Red;
    }

    const key = moduleName.toUpperCase() as keyof InverterUpgradeConfig;
    const modules = deviceUpgradeConfig?.[key] as ChipData[];

    modules?.forEach((fw) => {
      const latestVersion = fw.LATEST_VERSION;
      const comparison = compareInverterFWModuleVersions(row[moduleName], latestVersion);
      if (comparison === FirmwareComparison.LESS_THAN) {
        isLatest = false;
      }
    });
    return isLatest ? ColorTypeEnum.Green : ColorTypeEnum.Yellow;
  } catch (e) {
    return ColorTypeEnum.Red;
  }
};

const DEVICE_REGISTRY_FILTER_KEY = 'deviceRegistryFilters';

@CreateCustomBlockSizeType({
  'breakpoint-500': 500,
})
@Component({
  selector: 'lib-device-registry',
  templateUrl: './device-registry.component.html',
  styleUrls: ['./device-registry.component.scss'],
})
export class DeviceRegistryComponent implements ICustomBlock, AfterViewInit {
  @ViewChild('deviceIdColumn', { static: true }) deviceIdColumn!: TemplateRef<any>;
  @ViewChild('actionsColumn', { static: true }) actionsColumn!: TemplateRef<any>;
  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.updateColumnLabels();
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Output() goToAlertRegistry = new EventEmitter<string>();
  @Output() goToDevice = new EventEmitter<string>();

  defaultPaginationConfig = {
    pageSize: 10,
    pageSizeOptions: [10, 25, 50],
    length: 100,
  };

  paginationConfig: BehaviorSubject<typeof this.defaultPaginationConfig> = new BehaviorSubject(this.defaultPaginationConfig);

  columns: ColumnConfig[] = [];

  sort: Subject<RDSDevicesSortInput> = new Subject();
  pagination: BehaviorSubject<RDSPaginationInput> = new BehaviorSubject({ pageIndex: 0, pageSize: 10 });
  filter: Subject<RDSDevicesStatusFilter> = new Subject();
  data$ = combineLatest([
    this.pagination,
    this.sort.pipe(startWith(undefined)),
    this.filter.pipe(startWith({})),
  ]).pipe(
    switchMap(([pagination, sort, filter]) =>
      from(this.deviceRegistryService.getDevices(pagination, filter, sort)).pipe(
        tap((data) => this.paginationConfig.next({ ...this.paginationConfig.value, length: data?.totalCount || 0 })),
        startWith(null)
      )
    ),
    map((data) => data?.devices ?? null),
  );

  deviceTypeOptions = Object.entries(RDSDeviceTypeEnum).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' '),
  }));

  batteryModelOptions =  Object.entries(BatteryModelEnum).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' '),
  }));

  filterForm!: FormGroup;

  constructor(
    private readonly accessSupportService: AccessSupportService,
    private readonly cdr: ChangeDetectorRef,
    private readonly deviceRegistryService: DeviceRegistryService,
    private readonly fb: FormBuilder,
    readonly el: ElementRef,
    readonly elementRef: ElementRef,
    readonly renderer: Renderer,
    readonly route: ActivatedRoute,
    readonly router: Router,
    readonly systemsService: SystemsService,
  ) {
    this.onSubmit = this.onSubmit.bind(this);
    this.resetFilter = this.resetFilter.bind(this);
  }

  ngAfterViewInit(): void {
    this.updateColumnLabels();
    this.filterForm = this.fb.group({
      deviceId: [''],
      type: [null],
      isInAlarm: [false],
      batteryModel: [null],
      arm: [''],
      dspm: [''],
      dsps: [''],
      hasAzzurroEvents: [false]
    });

    const savedFilters = localStorage.getItem(DEVICE_REGISTRY_FILTER_KEY);
    if (savedFilters) {
      this.filterForm.patchValue(JSON.parse(savedFilters));
      this.filter.next(JSON.parse(savedFilters));
    }

    this.cdr.detectChanges();

  }

  onPageChange(event: PageEvent) {
    this.pagination.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  onSortChange(event: Sort) {
      this.pagination.next({ pageIndex: 0, pageSize: this.defaultPaginationConfig.pageSize });

      const sortColumn = event.active as keyof typeof RDSDeviceSortEnum;
      const direction = event.direction === 'asc' ? RDSSortDirectionEnum.ASC : RDSSortDirectionEnum.DESC;

      if (sortColumn in RDSDeviceSortEnum) {
        this.sort.next({
          column: RDSDeviceSortEnum[sortColumn],
          sortDirection: direction,
        });
      } else {
        this.sort.next(undefined);
      }
    }

  onSubmit(): void {
    if (this.filterForm.valid) {
      localStorage.setItem(DEVICE_REGISTRY_FILTER_KEY, JSON.stringify(this.filterForm.value));
      this.pagination.next({ pageIndex: 0, pageSize: this.defaultPaginationConfig.pageSize || 10 });
      const filters = this.filterForm.value;
      this.filter.next({
        deviceId: filters.deviceId,
        isInAlarm: filters.isInAlarm ? true : undefined,
        batteryModel: filters.batteryModel,
        arm: filters.arm,
        type: filters.type,
        dspm: filters.dspm,
        dsps: filters.dsps,
        hasAzzurroEvents: filters.hasAzzurroEvents ? true : undefined,
      });
    }
  }

  resetFilter(): void {
    this.pagination.next({ pageIndex: 0, pageSize: this.defaultPaginationConfig.pageSize || 10 });
    this.filterForm.reset();
    localStorage.removeItem(DEVICE_REGISTRY_FILTER_KEY);
    this.filter.next({});
  }


  updateColumnLabels(): void {
    this.columns = [
      {
        columnDef: 'deviceId',
        header: this.labels.deviceId,
        type: ColumnTypeEnum.Template,
        template: this.deviceIdColumn,
        sticky: 'start',
      },
      {
        columnDef: 'devType',
        header: this.labels.deviceType,
        type: ColumnTypeEnum.Text,
        cell: (row: RDSDeviceStatus) => row.devType,
        sortable: false,
      },
      {
        columnDef: 'isInAlarm',
        header: this.labels.alarms,
        type: ColumnTypeEnum.Icon,
        cell: (row: RDSDeviceStatus) => row.isInAlarm ? 'report' : 'verified',
        tooltip: (row: RDSDeviceStatus) => row.isInAlarm ? this.labels.isInAlarm : this.labels.noAlarm,
        color: (row: RDSDeviceStatus) => row.isInAlarm ? ColorTypeEnum.Red : ColorTypeEnum.Green,
        cellOutput: (value: RDSDeviceStatus) => this.goToAlertRegistry.emit(value.deviceId),
        sortable: false,
      },
      {
        columnDef: 'hasAzzurroEvents',
        header: this.labels.hasAzzurroEvents,
        type: ColumnTypeEnum.Icon,
        tooltip: (row: RDSDeviceStatus) => row.hasAzzurroEvents ? this.labels.azzurroEventPresent : this.labels.azzurroEventNotPresent,
        cell: (row: RDSDeviceStatus) => row.hasAzzurroEvents ? 'report' : 'verified',
        color: (row: RDSDeviceStatus) => row.hasAzzurroEvents ? ColorTypeEnum.Red : ColorTypeEnum.Green,
        sortable: false,
      },
      {
        columnDef: 'batteryModel',
        type: ColumnTypeEnum.Text,
        header: this.labels.batteryModel,
        cell: (row: RDSDeviceStatus) => row.batteryModel,
        sortable: false,
      },
      {
        columnDef: 'arm',
        header: 'ARM',
        type: ColumnTypeEnum.Chip,
        cell: (row: RDSDeviceStatus) => row.arm,
        cellHidden: (row: RDSDeviceStatus) => !row.arm,
        chipColor$: (row: RDSDeviceStatus) => getChipColor(row, 'arm', this.accessSupportService),
        sortable: false,
      },
      {
        columnDef: 'dspm',
        header: 'DSPM',
        type: ColumnTypeEnum.Chip,
        cell: (row: RDSDeviceStatus) => row.dspm,
        cellHidden: (row: RDSDeviceStatus) => !row.dspm,
        chipColor$: (row: RDSDeviceStatus) => getChipColor(row, 'arm', this.accessSupportService),
        sortable: false,
      },
      {
        columnDef: 'dsps',
        header: 'DSPS',
        type: ColumnTypeEnum.Chip,
        cell: (row: RDSDeviceStatus) => row.dsps,
        cellHidden: (row: RDSDeviceStatus) => !row.dsps,
        chipColor$: (row: RDSDeviceStatus) => getChipColor(row, 'arm', this.accessSupportService),
        sortable: false,
      },
      {
        columnDef: 'updated_at',
        header: this.labels.updatedAt,
        type: ColumnTypeEnum.Date,
        format: 'dd/MM/yyyy HH:mm',
        cell: (row: RDSDeviceStatus) => row.updatedAt,
        sortable: true,
      },
      {
        columnDef: 'actions',
        header: this.labels.actions,
        type: ColumnTypeEnum.Template,
        template: this.actionsColumn,
        sortable: false,
        sticky: 'end',
      },
    ];
  }
}
